import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowRight } from 'lucide-react';
import { FAQSchema } from '../../components/seo/SchemaMarkup';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';

export default function BachelorPartyJamshedpur() {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "Where is the best place for a bachelor party in Jamshedpur?",
      answer: "Scratch Unleashed at Bindal Mall, Sonari is widely regarded as the top bachelor party venue in Jamshedpur. The combination of premium bottle service, world-class DJ music, VIP tables, and private section booking makes it ideal for bachelor parties."
    },
    {
      question: "How much does a bachelor party package cost at Scratch Unleashed?",
      answer: "Bachelor party packages start from ₹15,000 for groups of 8-10 people, which includes a reserved premium table, two bottles of spirits with mixers, a dedicated host, and exclusive section access. Custom packages for larger groups are available on enquiry."
    },
    {
      question: "Can I pre-arrange surprises for the groom?",
      answer: "Absolutely! Scratch Unleashed's events team can arrange special touches like a sash/badge for the groom, custom 'last night of freedom' banners, birthday-style cake with a personalised message, and even special DJ shoutouts during the event."
    },
    {
      question: "How far in advance should I book a bachelor party?",
      answer: "We recommend booking at least 1-2 weeks in advance for weekend bachelor parties, and 3-5 days for weekday events. Peak dates (long weekends, festival periods, December-January) book up faster, so earlier is always better."
    }
  ];

  return (
    <>
      <FAQSchema faqs={faqs} />
      <div className="space-y-6 text-[var(--text-dim)] leading-relaxed">
        <p className="text-lg">
          Planning a <strong>bachelor party in Jamshedpur</strong> that the groom and the whole squad will actually remember? This guide covers everything — from choosing the right venue, planning the evening, must-have elements, and making sure the celebration lives up to the occasion. Spoiler: it starts and ends at <a href="/private-party-venue-jamshedpur" className="text-[var(--accent-gold)] hover:underline">Scratch Unleashed</a>.
        </p>

        <h2 className="font-bebas text-3xl mt-8 mb-4 text-[var(--text)]">Why Jamshedpur is a Great Destination for Bachelor Parties</h2>
        <p>
          Jamshedpur has transformed into a legitimate entertainment destination — and for groups planning a bachelor night, that's genuinely good news. The city's premium nightlife infrastructure, centered around the Sonari and Bistupur corridors, offers experiences that rival metropolitan venues without the logistical nightmare of travelling to Mumbai or Bangalore.
        </p>
        <p>
          For groups based in or visiting Jharkhand, Jamshedpur offers a concentrated evening: get dressed, go to one great venue, have an exceptional night, and get home safely. Scratch Unleashed makes that entire experience possible in one location — premium bar, world-class DJ, VIP table service, private section booking, and an atmosphere that signals you're somewhere serious about nightlife.
        </p>

        <h2 className="font-bebas text-3xl mt-8 mb-4 text-[var(--text)]">The Perfect Bachelor Party at Scratch Unleashed</h2>
        <p>
          Scratch Unleashed has designed its <strong>bachelor party package in Jamshedpur</strong> around what actually makes these nights memorable. When you book a private section, you get:
        </p>
        <ul className="list-disc list-inside space-y-2 pl-4">
          <li>A premium reserved table in the best section of the venue</li>
          <li>Dedicated table host who handles drink service throughout the night</li>
          <li>Welcome shots for the full party on arrival</li>
          <li>Choice of two premium spirit bottles with ice service and mixers</li>
          <li>Priority entry — no queue, straight to your table</li>
          <li>Optional add-ons: custom cake, sash/badge, shoutout from the DJ, photo moments</li>
        </ul>
        <p>
          The venue's <strong>Funktion-One sound system</strong> means the music quality is exceptional — not a phone plugged into a Bluetooth speaker, but genuine club-grade audio that makes every track sound exactly as intended. Whether the squad is into house, techno, hip-hop, or commercial hits, our DJs read the room and keep energy levels where they should be for a celebration.
        </p>

        <h2 className="font-bebas text-3xl mt-8 mb-4 text-[var(--text)]">Planning the Evening: A Timeline That Works</h2>
        <p>
          The best bachelor parties in Jamshedpur follow a loose structure that ensures the groom gets the full experience without the night burning out too early or starting too late:
        </p>
        <p><strong>7:00 PM – Pre-drinks at a lounge or hotel room.</strong> Get the squad together, take the group photos, do the first toast. This phase is about warming up and getting everyone in the same gear.</p>
        <p><strong>9:00 PM – Arrive at Scratch Unleashed.</strong> Your table is ready. Introductions to your host, first drink order, orientation. The energy in the venue is building at this point.</p>
        <p><strong>10:30 PM – Peak energy.</strong> The DJ is fully warmed up, the dance floor is busy, and the squad is having the best possible time. This is the phase where the groom's night becomes legendary.</p>
        <p><strong>1:00 AM onwards.</strong> The night winds down at your pace. Some groups leave around 1 AM; others stay until the venue closes around 3 AM. Either way, the celebration has delivered.</p>

        <h2 className="font-bebas text-3xl mt-8 mb-4 text-[var(--text)]">Booking Your Bachelor Party</h2>
        <p>
          Booking is straightforward: visit <a href="/checkin" className="text-[var(--accent-gold)] hover:underline">our booking page</a> or use the enquiry form to describe your requirements. Our events team will respond within 24 hours with a tailored proposal covering available dates, package options, and pricing.
        </p>
        <p>
          For groups of 15+, we recommend discussing full section or venue buyout options. These ensure your bachelor party isn't sharing a dance floor with strangers, and the entire evening feels exclusively yours.
        </p>

        <div className="bg-[var(--surface)] border border-[var(--border-custom)] rounded-2xl p-8 my-8">
          <h3 className="font-bebas text-2xl mb-4 text-[var(--text)]">Ready to Plan the Bachelor Party?</h3>
          <p className="text-[var(--text-dim)] mb-6">
            Contact Scratch Unleashed and let our events team handle everything. The groom deserves the best send-off in Jamshedpur.
          </p>
          <Button className="btn-crimson-glow rounded-xl" onClick={() => navigate('/private-party-venue-jamshedpur')}>
            View Private Event Packages
            <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>

        <h2 className="font-bebas text-3xl mt-12 mb-6 text-[var(--text)]">Frequently Asked Questions</h2>
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((faq, index) => (
            <AccordionItem key={index} value={`item-${index}`} className="border border-[var(--border-custom)] rounded-xl px-4 bg-[var(--surface)]">
              <AccordionTrigger className="hover:no-underline text-left">
                <span className="font-semibold text-[var(--text)]">{faq.question}</span>
              </AccordionTrigger>
              <AccordionContent className="text-sm text-[var(--text-dim)] pb-4">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </>
  );
}
